/**
 * Derived view-state — spec §4 (D2). Identical truth table on iOS,
 * Android, and Web. A single pure function maps the existing inputs
 * (`station?`, `isLoading`, `isOffline`) to one of three rendering
 * branches; the React composition root selects the matching UI.
 *
 *   loading        — station == nil && isLoading            → S3 skeleton
 *   error          — station == nil && !isLoading           → S4 empty/error
 *   content(offline) — station != nil; offline = isOffline  → S1/S2 (+ S5 banner)
 *
 * Precedence: a resolvable station (`hasStation`) always wins — it can
 * never collapse back to the skeleton mid-refetch. Offline is meaningful
 * only inside `content`; offline with no resolvable station is `error`
 * (spec §4), not an offline banner.
 */
export type ViewState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'content'; offline: boolean }

export interface ViewStateInput {
  /** True when a renderable station has resolved (iOS `station != nil`). */
  hasStation: boolean
  /** Host station load in flight (iOS `isLoading`). */
  isLoading: boolean
  /** Reachability signal (iOS `isOffline`). */
  isOffline: boolean
}

export function deriveViewState({ hasStation, isLoading, isOffline }: ViewStateInput): ViewState {
  if (hasStation) {
    return { kind: 'content', offline: isOffline }
  }
  if (isLoading) {
    return { kind: 'loading' }
  }
  return { kind: 'error' }
}
